import { Controller } from '@hotwired/stimulus';

/**
 * SelectTextCaptureController
 *
 * Captures data from a selected option and stores it in target fields.
 * Supports capturing option text and any data attributes from the selected option.
 *
 * @example Basic Usage - Capture option text
 * <div data-controller="select-text-capture">
 *   <select data-select-text-capture-target="source"
 *           data-action="change->select-text-capture#capture">
 *     <option value="1">Option One</option>
 *     <option value="2">Option Two</option>
 *   </select>
 *
 *   <input type="hidden"
 *          data-select-text-capture-target="destination" />
 * </div>
 *
 * @example Advanced Usage - Capture multiple data attributes
 * <div data-controller="select-text-capture">
 *   <select data-select-text-capture-target="source"
 *           data-action="change->select-text-capture#capture">
 *     <option value="1" data-description="Location One" data-address="123 Main St">Option One</option>
 *     <option value="2" data-description="Location Two" data-address="456 Oak Ave">Option Two</option>
 *   </select>
 *
 *   <!-- Capture the data-description attribute -->
 *   <input type="hidden"
 *          data-select-text-capture-target="destination"
 *          data-capture-attribute="description" />
 *
 *   <!-- Capture the data-address attribute -->
 *   <input type="hidden"
 *          data-select-text-capture-target="destination"
 *          data-capture-attribute="address" />
 *
 *   <!-- Capture the option text (no data-capture-attribute) -->
 *   <input type="hidden"
 *          data-select-text-capture-target="destination" />
 * </div>
 *
 * Targets:
 * - source: The select element to monitor
 * - destination: The field(s) where captured data will be stored
 *   - Can have multiple destination targets
 *   - Use data-capture-attribute to specify which data attribute to capture
 *   - If no data-capture-attribute is specified, captures the option text
 *
 * Actions:
 * - capture: Captures data from the selected option and stores it in destination fields
 */
export default class extends Controller {
  static targets = ['source', 'destination'];

  connect() {
    // Capture initial value if one is already selected
    this.capture();
  }

  capture() {
    if (!this.hasSourceTarget) return;

    const selectedOption = this.sourceTarget.options[this.sourceTarget.selectedIndex];
    if (!selectedOption) return;

    // Update each destination target
    this.destinationTargets.forEach((destination) => {
      const attribute = destination.dataset.captureAttribute;

      if (attribute) {
        // Capture data attribute (e.g., data-description -> description)
        destination.value = selectedOption.dataset[attribute] || '';
      } else {
        // Capture option text (default behavior)
        destination.value = selectedOption.text;
      }
    });
  }
}
